import { gql } from "@apollo/client";
import { useContext, useEffect } from "react";
import { BgwContext } from "../../../contexts/backend_gateway/context";
import {
  useCreateMyGitConnectionMutation,
  useGetMyGitConnectionsQuery,
} from "../../../services/backend_gateway/__generated__/backend_gateway-types";
import { GitRepositoriesComponent } from "./component";
import { AlertsContext } from "../../../contexts/alerts/context";
import { AlertType } from "../../../contexts/alerts/type";

export const GitRepositoriesContainer = (props: {
  deploymentId: string;
  navigateToHelp: (stepId: string) => void;
  setGitConnectionExists: () => void;
}) => {
  const { deploymentId, navigateToHelp, setGitConnectionExists } = props;
  const { bgwService } = useContext(BgwContext);
  const { addAlert } = useContext(AlertsContext);

  const {
    data: getMyGitConnectionsData,
    loading: getMyGitConnectionsLoading,
    refetch: getMyGitConnectionsRefetch,
  } = useGetMyGitConnectionsQuery({
    client: bgwService.getClient(),
    variables: {
      deploymentId,
    },
    onError: (error) => {
      if (error?.message !== undefined) {
        addAlert({
          text: error.message!,
          type: AlertType.WARNING,
        });
      }
    },
  });
  const [
    createMyGitConnectionMutation,
    { loading: createMyGitConnectionLoading },
  ] = useCreateMyGitConnectionMutation({
    client: bgwService.getClient(),
    onError: (error) => {
      if (error?.message !== undefined) {
        addAlert({
          text: error.message!,
          type: AlertType.WARNING,
        });
      }
    },
  });

  const createGitConnection = (name: string, providerType: string) => {
    createMyGitConnectionMutation({
      variables: {
        gitConnectionInput: {
          deploymentId,
          name,
          providerType,
        },
      },
      update(cache, { data }) {
        cache.modify({
          fields: {
            getMyGitConnections(existingGitConnections = []) {
              const newGitConnectionsRef = cache.writeFragment({
                data: data?.createMyGitConnection,
                fragment: gql`
                  fragment NewGitConnection on GitConnection {
                    id
                    name
                    connectionStatus
                    ownerAccountId
                    providerType
                    pendingConnectionLink
                  }
                `,
              });
              return [...existingGitConnections, newGitConnectionsRef];
            },
          },
        });
      },
      refetchQueries: ["GetMyGitConnections"],
      onCompleted: (data) => {
        if (data?.createMyGitConnection !== undefined) {
          // ...
        }
      },
    });
  };

  useEffect(() => {
    let intervalId = setInterval(async () => {
      if (
        getMyGitConnectionsData?.getMyGitConnections
          ?.map((c) => c.connectionStatus)
          .includes("PENDING")
      ) {
        const data = await getMyGitConnectionsRefetch();
        if (
          data?.data?.getMyGitConnections
            ?.map((c) => c.connectionStatus)
            .includes("AVAILABLE")
        ) {
          setGitConnectionExists();
        }
      }
    }, 10000);
    return () => clearInterval(intervalId);
  }, [
    getMyGitConnectionsData,
    getMyGitConnectionsRefetch,
    setGitConnectionExists,
  ]);

  return (
    <GitRepositoriesComponent
      loading={getMyGitConnectionsLoading || createMyGitConnectionLoading}
      createGitConnection={createGitConnection}
      connections={getMyGitConnectionsData?.getMyGitConnections || []}
      navigateToHelp={() => navigateToHelp("git_connections")}
    />
  );
};
